import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { calcTotals, money, IMAGES, NPN } from "@/components/store/data";

export default function BasketDrawer({ open, onClose, qty, setQty, onCheckout }) {
  const { subtotal, discount, rate, shipping, total } = calcTotals(qty);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="basket-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[96] w-full max-w-md bg-[#08302D] border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <span className="font-display text-xl text-[#F4F1EB]">Your Basket</span>
              <button data-testid="close-basket-btn" onClick={onClose} className="text-[#B8C5C4] hover:text-[#F4F1EB]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {qty < 1 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-[#B8C5C4] gap-4">
                  <ShoppingBag className="w-10 h-10 opacity-40" strokeWidth={1} />
                  <p data-testid="empty-basket-msg">Your basket has 0 bottles.</p>
                </div>
              ) : (
                <div className="flex gap-4">
                  <img src={IMAGES.heroBottle} alt="bottle" className="w-20 h-20 object-cover rounded-sm border border-white/10" />
                  <div className="flex-1">
                    <p className="font-display text-lg text-[#F4F1EB]">Ubiquinol CoQ10</p>
                    <p className="text-xs text-[#B8C5C4] mb-3">NPN {NPN} · {money(38)}/bottle</p>
                    <div className="flex items-center gap-3">
                      <button data-testid="drawer-decrement" onClick={() => setQty((q) => Math.max(0, q - 1))} className="w-8 h-8 flex items-center justify-center border border-white/20 text-[#F4F1EB] hover:border-[#D4AF37]">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span data-testid="drawer-qty" className="text-[#F4F1EB] w-6 text-center">{qty}</span>
                      <button data-testid="drawer-increment" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 flex items-center justify-center border border-white/20 text-[#F4F1EB] hover:border-[#D4AF37]">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 px-6 py-6 space-y-3">
              <div className="flex justify-between text-sm text-[#B8C5C4]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-[#63A375]"><span>Volume discount ({Math.round(rate * 100)}%)</span><span>-{money(discount)}</span></div>
              )}
              <div className="flex justify-between text-sm text-[#B8C5C4]"><span>Shipping</span><span>{money(shipping)}</span></div>
              <div className="flex justify-between items-baseline pt-3 border-t border-white/10">
                <span className="text-sm uppercase tracking-[0.15em] text-[#F4F1EB]">Total</span>
                <span className="font-display text-2xl text-[#D4AF37]">{money(total)} CAD</span>
              </div>
              <button
                data-testid="drawer-checkout-btn"
                disabled={qty < 1}
                onClick={onCheckout}
                className="w-full mt-2 bg-[#D4AF37] text-[#052221] py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#F0C851] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Checkout
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
